import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { apiClient } from "@/lib/api";

const initialForm = {
  name: "",
  position_size_pct: 2,
  atr_stop_multiplier: 1.5,
  // 2026-06-09: RR yerine doğrudan "Kar Al %" girilir. Default %2.
  take_profit_pct: 2,
  // 2026-06-11 v7: SL ROI tabanlı + ATR floor (TP ile simetrik + wick koruma).
  stop_loss_pct: 4,
  stop_loss_atr_floor: 0.8,
  daily_loss_cutoff_pct: 5,
  max_open_positions: 3,
  max_leverage: 3,
  spread_limit_bps: 30,
  slippage_limit_bps: 40,
  min_liquidity_usdt: 100000,
  direction_scope: "both",
  bot_profile_id: "",
};

// 2026-02-13: Scope etiket ve rozet renkleri (multi-active 4 scope desteği).
const SCOPE_LABEL = {
  both: "BOTH",
  long_only: "LONG",
  short_only: "SHORT",
  spot_only: "SPOT",
};
const SCOPE_BADGE_CLASS = {
  both: "bg-indigo-600 text-white",
  long_only: "bg-emerald-600 text-white",
  short_only: "bg-rose-600 text-white",
  spot_only: "bg-amber-500 text-black",
};

export const RiskPoliciesPage = () => {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [formErrors, setFormErrors] = useState({});
  const [reasonById, setReasonById] = useState({});
  const [previewImpact, setPreviewImpact] = useState(null);
  const [selectedPolicy, setSelectedPolicy] = useState(null);
  const [history, setHistory] = useState([]);
  // 2026-06-02 P0 #8: Bot-level policy seçimi için bot listesi.
  const [botProfiles, setBotProfiles] = useState([]);

  const activePolicy = useMemo(() => (items || []).find((item) => item.is_active) || null, [items]);
  // 2026-02-13: Multi-active risk policies (4 scope: spot_only, long_only, short_only, both).
  // Eski davranış sadece ilk aktifi gösterdiği için kullanıcıya "diğerleri kayboldu" hissi
  // veriyordu. Artık tüm aktifleri scope'larıyla birlikte gösteriyoruz.
  const activePolicies = useMemo(() => (items || []).filter((item) => item.is_active), [items]);

  const fetchItems = async () => {
    const { data } = await apiClient.get("/risk-policies");
    setItems(data);
  };

  const fetchBotProfiles = async () => {
    try {
      const { data } = await apiClient.get("/bot-profiles");
      setBotProfiles(Array.isArray(data) ? data : []);
    } catch {
      setBotProfiles([]);
    }
  };

  useEffect(() => {
    fetchItems();
    fetchBotProfiles();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    const nextErrors = {};
    if (!form.name.trim()) {
      nextErrors.name = "Policy Name zorunludur.";
    }
    if (!Number(form.position_size_pct) || Number(form.position_size_pct) <= 0) {
      nextErrors.position_size_pct = "Position Size (%) 0'dan büyük olmalı.";
    }
    if (!Number(form.atr_stop_multiplier) || Number(form.atr_stop_multiplier) <= 0) {
      nextErrors.atr_stop_multiplier = "ATR Multiplier 0'dan büyük olmalı.";
    }
    if (!Number(form.take_profit_pct) || Number(form.take_profit_pct) <= 0 || Number(form.take_profit_pct) > 100) {
      nextErrors.take_profit_pct = "Kar Al (%) 0-100 arasında olmalı.";
    }
    if (!Number(form.stop_loss_pct) || Number(form.stop_loss_pct) <= 0 || Number(form.stop_loss_pct) > 100) {
      nextErrors.stop_loss_pct = "Zarar Kes (%) 0-100 arasında olmalı.";
    }
    if (Number(form.stop_loss_atr_floor) < 0 || Number(form.stop_loss_atr_floor) > 5) {
      nextErrors.stop_loss_atr_floor = "ATR Floor 0-5 arasında olmalı (0 = floor kapalı).";
    }
    if (!Number(form.max_open_positions) || Number(form.max_open_positions) < 1) {
      nextErrors.max_open_positions = "Max Concurrent Trades en az 1 olmalı.";
    }
    if (!Number(form.daily_loss_cutoff_pct) || Number(form.daily_loss_cutoff_pct) <= 0) {
      nextErrors.daily_loss_cutoff_pct = "Max Daily Loss (%) 0'dan büyük olmalı.";
    }

    setFormErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      toast.error("Form alanlarını kontrol edin");
      return;
    }

    const payload = {
      name: form.name.trim(),
      position_size_pct: Number(form.position_size_pct),
      atr_stop_multiplier: Number(form.atr_stop_multiplier),
      // 2026-06-09: UI artık RR göndermiyor. Backend default (1.8) ile geriye uyum.
      take_profit_pct: Number(form.take_profit_pct),
      stop_loss_pct: Number(form.stop_loss_pct),
      stop_loss_atr_floor: Number(form.stop_loss_atr_floor),
      daily_loss_cutoff_pct: Number(form.daily_loss_cutoff_pct),
      max_open_positions: Number(form.max_open_positions),
      max_leverage: Number(form.max_leverage),
      spread_limit_bps: Number(form.spread_limit_bps),
      slippage_limit_bps: Number(form.slippage_limit_bps),
      min_liquidity_usdt: Number(form.min_liquidity_usdt),
      direction_scope: String(form.direction_scope || "both"),
      bot_profile_id: form.bot_profile_id ? String(form.bot_profile_id) : null,
      reason_note: reasonById[editingId || 'draft'] || 'manual_update',
    };

    try {
      if (editingId) {
        await apiClient.put(`/risk-policies/${editingId}`, payload);
        toast.success("Risk policy güncellendi");
      } else {
        await apiClient.post("/risk-policies", payload);
        toast.success("Risk policy oluşturuldu");
      }
      setEditingId(null);
      setForm(initialForm);
      setFormErrors({});
      await fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Risk policy işlemi başarısız");
    }
  };

  const editPolicy = (item) => {
    setEditingId(item.id);
    // 2026-06-09: Eski politikalarda take_profit_pct NULL olabilir → default 2% göster.
    // 2026-06-11 v7: stop_loss_pct ve stop_loss_atr_floor — default 4 ve 0.8.
    setForm({
      ...item,
      take_profit_pct: item.take_profit_pct ?? 2,
      stop_loss_pct: item.stop_loss_pct ?? 4,
      stop_loss_atr_floor: item.stop_loss_atr_floor ?? 0.8,
      bot_profile_id: item.bot_profile_id || "",
    });
    setFormErrors({});
    setSelectedPolicy(item);
  };

  const requireReason = (id) => {
    const value = String(reasonById[id] || '').trim();
    if (!value) {
      toast.error('Reason zorunlu');
      return null;
    }
    return value;
  };

  const previewPolicyImpact = async (item) => {
    try {
      const { data } = await apiClient.post(`/risk-policies/${item.id}/preview-impact`, {
        current_daily_pnl_pct: 1.5,
        current_open_positions: 1,
        current_leverage: 2,
        current_spread_bps: 12,
        current_slippage_bps: 10,
      });
      setPreviewImpact(data);
      setSelectedPolicy(item);
      toast.success('Risk impact preview hazır');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Impact preview başarısız');
    }
  };

  const activatePolicy = async (item) => {
    const reason = requireReason(item.id);
    if (!reason) return;
    try {
      await apiClient.post(`/risk-policies/${item.id}/activate`, { reason });
      await fetchItems();
      toast.success('Policy active oldu');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Policy activate başarısız');
    }
  };

  const loadHistory = async (item) => {
    try {
      const { data } = await apiClient.get(`/risk-policies/${item.id}/history`);
      setHistory(data?.items || []);
      setSelectedPolicy(item);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Policy history yüklenemedi');
    }
  };

  const rollbackPolicy = async (item) => {
    const reason = requireReason(item.id);
    if (!reason) return;
    try {
      await apiClient.post(`/risk-policies/${item.id}/rollback`, { reason });
      await fetchItems();
      toast.success('Policy rollback tamamlandı');
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Policy rollback başarısız');
    }
  };

  const deletePolicy = async (item) => {
    const ok = window.confirm(`${item.name} politikasını kalıcı silmek istediğinize emin misiniz?`);
    if (!ok) return;

    try {
      await apiClient.delete(`/risk-policies/${item.id}`);
      if (editingId === item.id) {
        setEditingId(null);
        setForm(initialForm);
        setFormErrors({});
      }
      if (selectedPolicy?.id === item.id) {
        setSelectedPolicy(null);
        setHistory([]);
        setPreviewImpact(null);
      }
      toast.success("Risk policy silindi");
      await fetchItems();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Risk policy silinemedi");
    }
  };

  return (
    <section className="space-y-4" data-testid="risk-policies-page">
      <header className="border border-slate-800 bg-slate-900 p-4" data-testid="risk-policies-header">
        <h2 className="text-4xl font-black uppercase tracking-tight" data-testid="risk-policies-title">Risk Policy Yönetimi</h2>
        <p className="mt-2 text-sm text-slate-400" data-testid="risk-policies-description">Position sizing, ATR Stop ve Kar Al (%) ile günlük risk limitleri tek formda yönetilir.</p>
        <div className="mt-3 rounded border border-cyan-800/40 bg-cyan-950/20 p-3" data-testid="risk-policies-active-indicator-panel">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-cyan-300" data-testid="risk-policies-active-summary">
            Aktif Politikalar: {activePolicies.length} {activePolicies.length > 0 ? `(${activePolicies.map((p) => SCOPE_LABEL[p.direction_scope] || p.direction_scope).join(' · ')})` : ''}
          </p>
          {activePolicies.length === 0 ? (
            <p className="text-xs text-slate-400" data-testid="risk-policies-active-empty">no active policy selected — yeni politika oluşturup aktif edin.</p>
          ) : (
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-4" data-testid="risk-policies-active-list">
              {activePolicies.map((policy) => (
                <div key={policy.id} className="rounded border border-emerald-700/40 bg-emerald-950/20 p-2" data-testid={`risk-policies-active-card-${policy.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${SCOPE_BADGE_CLASS[policy.direction_scope] || 'bg-slate-700 text-slate-100'}`} data-testid={`risk-policies-active-scope-${policy.id}`}>
                      {SCOPE_LABEL[policy.direction_scope] || policy.direction_scope}
                    </span>
                    <span className="text-[10px] text-slate-400">v{policy.version_num}</span>
                  </div>
                  <p className="mt-1 truncate text-xs font-semibold text-slate-100" data-testid={`risk-policies-active-name-${policy.id}`}>{policy.name}</p>
                  <p className="text-[10px] text-slate-400" data-testid={`risk-policies-active-bot-${policy.id}`}>
                    {policy.bot_profile_id ? `Bot: ${(botProfiles.find((b) => b.id === policy.bot_profile_id)?.name || policy.bot_profile_id.slice(0, 8))}` : 'Tüm Botlar (user-default)'}
                  </p>
                  <p className="text-[10px] text-slate-400" data-testid={`risk-policies-active-tp-sl-${policy.id}`}>
                    TP {policy.take_profit_pct}% · SL {policy.stop_loss_pct}% · Lev {policy.max_leverage}x
                  </p>
                </div>
              ))}
            </div>
          )}
          <p className="mt-2 text-[11px] text-amber-300/80" data-testid="risk-policies-active-info-banner">
            💡 Aynı (Bot, Yön Kapsamı) için 2.policy aktif edilirse eskisi otomatik <strong>superseded</strong> olur. 4 farklı yön (Spot / Long / Short / Both) için ayrı policy oluşturup hepsini aktif tutabilirsiniz.
          </p>
        </div>
      </header>

      <form onSubmit={handleSubmit} className="grid gap-3 border border-slate-800 bg-slate-900 p-4 md:grid-cols-2" data-testid="risk-policy-form">
        <div className="form-group" data-testid="risk-form-group-name">
          <label className="form-label" htmlFor="risk-form-name-input" data-testid="risk-form-name-label">Policy Name</label>
          <Input
            id="risk-form-name-input"
            value={form.name}
            onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            data-testid="risk-form-name-input"
            aria-label="Policy Name"
            aria-describedby="risk-form-name-helper risk-form-name-error"
            required
          />
          <p className="form-helper-text" id="risk-form-name-helper" data-testid="risk-form-name-helper">Risk politikasını ayırt etmek için açık bir ad girin.</p>
          {formErrors.name && <p className="form-error-text" id="risk-form-name-error" data-testid="risk-form-name-error">{formErrors.name}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-position-size">
          <label className="form-label" htmlFor="risk-form-position-size-input" data-testid="risk-form-position-size-label">Position Size (%)</label>
          <Input
            id="risk-form-position-size-input"
            type="number"
            step="0.1"
            value={form.position_size_pct}
            onChange={(event) => setForm((prev) => ({ ...prev, position_size_pct: event.target.value }))}
            data-testid="risk-form-position-size-input"
            aria-label="Position Size (%)"
            aria-describedby="risk-form-position-size-helper risk-form-position-size-error"
            required
          />
          <p className="form-helper-text" id="risk-form-position-size-helper" data-testid="risk-form-position-size-helper">Her işlemde kullanılacak pozisyon yüzdesi.</p>
          {formErrors.position_size_pct && <p className="form-error-text" id="risk-form-position-size-error" data-testid="risk-form-position-size-error">{formErrors.position_size_pct}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-atr">
          <label className="form-label" htmlFor="risk-form-atr-input" data-testid="risk-form-atr-label">Stop Loss · ATR Çarpanı</label>
          <Input
            id="risk-form-atr-input"
            type="number"
            step="0.1"
            value={form.atr_stop_multiplier}
            onChange={(event) => setForm((prev) => ({ ...prev, atr_stop_multiplier: event.target.value }))}
            data-testid="risk-form-atr-input"
            aria-label="Stop Loss ATR Multiplier"
            aria-describedby="risk-form-atr-helper risk-form-atr-error"
            required
          />
          <p className="form-helper-text" id="risk-form-atr-helper" data-testid="risk-form-atr-helper">
            Örn: <span className="font-mono">1.5</span> girerseniz, Stop Loss giriş fiyatından <span className="font-mono">1.5 × ATR</span> mesafede konur.
          </p>
          {formErrors.atr_stop_multiplier && <p className="form-error-text" id="risk-form-atr-error" data-testid="risk-form-atr-error">{formErrors.atr_stop_multiplier}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-tp-pct">
          <label className="form-label" htmlFor="risk-form-tp-pct-input" data-testid="risk-form-tp-pct-label">Kar Al · ROI Yüzdesi (%)</label>
          <Input
            id="risk-form-tp-pct-input"
            type="number"
            step="0.1"
            min="0.1"
            max="100"
            value={form.take_profit_pct}
            onChange={(event) => setForm((prev) => ({ ...prev, take_profit_pct: event.target.value }))}
            data-testid="risk-form-tp-pct-input"
            aria-label="Kar Al ROI Yüzdesi"
            aria-describedby="risk-form-tp-pct-helper risk-form-tp-pct-error"
            required
          />
          <p className="form-helper-text" id="risk-form-tp-pct-helper" data-testid="risk-form-tp-pct-helper">
            Margin üzerinden net getiri (ROI). Kaldıraçtan bağımsız: <span className="font-mono">%2</span> girerseniz, 1x'te de 10x'te de margin'inizin <span className="font-mono">%2</span>'si kâr olunca işlem otomatik kapanır. Yüksek kaldıraçta fiyat hareketi daha küçük olur (örn 5x'te %2 ROI = %0.4 fiyat hareketi).
          </p>
          {formErrors.take_profit_pct && <p className="form-error-text" id="risk-form-tp-pct-error" data-testid="risk-form-tp-pct-error">{formErrors.take_profit_pct}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-sl-pct">
          <label className="form-label" htmlFor="risk-form-sl-pct-input" data-testid="risk-form-sl-pct-label">Zarar Kes · ROI Yüzdesi (%)</label>
          <Input
            id="risk-form-sl-pct-input"
            type="number"
            step="0.1"
            min="0.1"
            max="100"
            value={form.stop_loss_pct}
            onChange={(event) => setForm((prev) => ({ ...prev, stop_loss_pct: event.target.value }))}
            data-testid="risk-form-sl-pct-input"
            aria-label="Zarar Kes ROI Yüzdesi"
            aria-describedby="risk-form-sl-pct-helper risk-form-sl-pct-error"
            required
          />
          <p className="form-helper-text" id="risk-form-sl-pct-helper" data-testid="risk-form-sl-pct-helper">
            Margin üzerinden max kayıp (ROI). TP ile simetrik mantık. Önerilen TP/SL oranı 1:2 (örn TP=%2, SL=%4). RR oranınız = TP/SL.
          </p>
          {formErrors.stop_loss_pct && <p className="form-error-text" id="risk-form-sl-pct-error" data-testid="risk-form-sl-pct-error">{formErrors.stop_loss_pct}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-sl-atr-floor">
          <label className="form-label" htmlFor="risk-form-sl-atr-floor-input" data-testid="risk-form-sl-atr-floor-label">SL · ATR Floor (Wick Koruması)</label>
          <Input
            id="risk-form-sl-atr-floor-input"
            type="number"
            step="0.1"
            min="0"
            max="5"
            value={form.stop_loss_atr_floor}
            onChange={(event) => setForm((prev) => ({ ...prev, stop_loss_atr_floor: event.target.value }))}
            data-testid="risk-form-sl-atr-floor-input"
            aria-label="SL ATR Floor"
            aria-describedby="risk-form-sl-atr-floor-helper risk-form-sl-atr-floor-error"
            required
          />
          <p className="form-helper-text" id="risk-form-sl-atr-floor-helper" data-testid="risk-form-sl-atr-floor-helper">
            ROI SL hesabı ATR × bu çarpanın altındaysa, SL ATR&apos;a genişletilir (mikro fitil / wick stop-out koruması). <span className="font-mono">0.8</span> = ortanca / önerilen. <span className="font-mono">0</span> = floor kapalı (saf ROI). Volatil coinlerde wick&apos;e karşı kalkan.
          </p>
          {formErrors.stop_loss_atr_floor && <p className="form-error-text" id="risk-form-sl-atr-floor-error" data-testid="risk-form-sl-atr-floor-error">{formErrors.stop_loss_atr_floor}</p>}
        </div>


        <div className="form-group" data-testid="risk-form-group-max-open">
          <label className="form-label" htmlFor="risk-form-max-open-input" data-testid="risk-form-max-open-label">Max Concurrent Trades</label>
          <Input
            id="risk-form-max-open-input"
            type="number"
            value={form.max_open_positions}
            onChange={(event) => setForm((prev) => ({ ...prev, max_open_positions: event.target.value }))}
            data-testid="risk-form-max-open-input"
            aria-label="Max Concurrent Trades"
            aria-describedby="risk-form-max-open-helper risk-form-max-open-error"
            required
          />
          <p className="form-helper-text" id="risk-form-max-open-helper" data-testid="risk-form-max-open-helper">Aynı anda açık kalabilecek işlem sayısı üst limiti.</p>
          {formErrors.max_open_positions && <p className="form-error-text" id="risk-form-max-open-error" data-testid="risk-form-max-open-error">{formErrors.max_open_positions}</p>}
        </div>

        <div className="form-group" data-testid="risk-form-group-daily-loss">
          <label className="form-label" htmlFor="risk-form-cutoff-input" data-testid="risk-form-cutoff-label">Max Daily Loss (%)</label>
          <Input
            id="risk-form-cutoff-input"
            type="number"
            step="0.1"
            value={form.daily_loss_cutoff_pct}
            onChange={(event) => setForm((prev) => ({ ...prev, daily_loss_cutoff_pct: event.target.value }))}
            data-testid="risk-form-cutoff-input"
            aria-label="Max Daily Loss (%)"
            aria-describedby="risk-form-cutoff-helper risk-form-cutoff-error"
            required
          />
          <p className="form-helper-text" id="risk-form-cutoff-helper" data-testid="risk-form-cutoff-helper">Günlük kayıp yüzdesi bu değeri aşarsa yeni işlem engellenir.</p>
          {formErrors.daily_loss_cutoff_pct && <p className="form-error-text" id="risk-form-cutoff-error" data-testid="risk-form-cutoff-error">{formErrors.daily_loss_cutoff_pct}</p>}
        </div>

        {/* 2026-06-02 P0 #7: Long/Short ayrı policy seçimi. */}
        <div className="form-group" data-testid="risk-form-group-direction-scope">
          <label className="form-label" htmlFor="risk-form-direction-scope-select" data-testid="risk-form-direction-scope-label">Yön Kapsamı</label>
          <select
            id="risk-form-direction-scope-select"
            className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            value={form.direction_scope}
            onChange={(event) => setForm((prev) => ({ ...prev, direction_scope: event.target.value }))}
            data-testid="risk-form-direction-scope-select"
          >
            <option value="both">Hem Long Hem Short</option>
            <option value="long_only">Sadece Long</option>
            <option value="short_only">Sadece Short</option>
            <option value="spot_only">Sadece Spot</option>
          </select>
          <p className="form-helper-text" data-testid="risk-form-direction-scope-helper">Spot / Long / Short / Both için ayrı policy oluşturup 4&apos;ünü de aktif tutabilirsiniz. Aynı (Bot, Yön) için 2.policy aktif edilirse eskisi superseded olur.</p>
        </div>

        {/* 2026-06-02 P0 #8: Bot-bazlı policy seçimi. */}
        <div className="form-group" data-testid="risk-form-group-bot-profile">
          <label className="form-label" htmlFor="risk-form-bot-profile-select" data-testid="risk-form-bot-profile-label">Hangi Bot İçin?</label>
          <select
            id="risk-form-bot-profile-select"
            className="w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100"
            value={form.bot_profile_id || ""}
            onChange={(event) => setForm((prev) => ({ ...prev, bot_profile_id: event.target.value }))}
            data-testid="risk-form-bot-profile-select"
          >
            <option value="">Tüm Botlar (User-level default)</option>
            {(botProfiles || []).map((bp) => (
              <option key={bp.id} value={bp.id}>{bp.name || bp.id?.slice(0, 8)}</option>
            ))}
          </select>
          <p className="form-helper-text" data-testid="risk-form-bot-profile-helper">Spesifik bir bot seçerseniz bu policy yalnız o bot için aktif olur; diğer botlar user-level default&apos;a düşer.</p>
        </div>

        <div className="flex gap-2 md:col-span-2">
          <Input value={reasonById[editingId || 'draft'] || ''} onChange={(event) => setReasonById((prev) => ({ ...prev, [editingId || 'draft']: event.target.value }))} placeholder="reason note" data-testid="risk-form-reason-input" />
          <Button type="submit" className="bg-orange-500 text-black hover:bg-orange-600" data-testid="risk-form-submit-button">
            {editingId ? "Güncelle" : "Oluştur"}
          </Button>
          {editingId && (
            <Button
              type="button"
              variant="outline"
              className="border-slate-700 bg-transparent text-slate-200"
              onClick={() => {
                setEditingId(null);
                setForm(initialForm);
                setFormErrors({});
              }}
              data-testid="risk-form-cancel-edit-button"
            >
              İptal
            </Button>
          )}
        </div>
      </form>

      <div className="border border-slate-800 bg-slate-900" data-testid="risk-policies-table-wrapper">
        <Table data-testid="risk-policies-table">
          <TableHeader>
            <TableRow>
              <TableHead data-testid="risk-table-head-name">Ad</TableHead>
              <TableHead data-testid="risk-table-head-scope">Yön</TableHead>
              <TableHead data-testid="risk-table-head-bot">Bot</TableHead>
              <TableHead data-testid="risk-table-head-position">Position %</TableHead>
              <TableHead data-testid="risk-table-head-atr">ATR Stop</TableHead>
              <TableHead data-testid="risk-table-head-tp-pct">Kar Al ROI %</TableHead>
              <TableHead data-testid="risk-table-head-status">Status</TableHead>
              <TableHead data-testid="risk-table-head-version">Version</TableHead>
              <TableHead data-testid="risk-table-head-action">Aksiyon</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id} data-testid={`risk-table-row-${item.id}`}>
                <TableCell data-testid={`risk-table-name-${item.id}`}>{item.name}</TableCell>
                <TableCell data-testid={`risk-table-scope-${item.id}`}>
                  <span className={`rounded px-2 py-0.5 text-[10px] font-bold ${SCOPE_BADGE_CLASS[item.direction_scope] || 'bg-slate-700 text-slate-100'}`} data-testid={`risk-table-scope-badge-${item.id}`}>
                    {SCOPE_LABEL[item.direction_scope] || item.direction_scope}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-slate-300" data-testid={`risk-table-bot-${item.id}`}>
                  {item.bot_profile_id ? (botProfiles.find((b) => b.id === item.bot_profile_id)?.name || item.bot_profile_id.slice(0, 8)) : 'Tüm Botlar'}
                </TableCell>
                <TableCell className="font-mono" data-testid={`risk-table-position-${item.id}`}>{item.position_size_pct}</TableCell>
                <TableCell className="font-mono" data-testid={`risk-table-atr-${item.id}`}>{item.atr_stop_multiplier}</TableCell>
                <TableCell className="font-mono" data-testid={`risk-table-tp-pct-${item.id}`}>{item.take_profit_pct ?? '—'}</TableCell>
                <TableCell data-testid={`risk-table-status-${item.id}`}>
                  <span className={`rounded px-2 py-1 text-xs font-semibold ${item.is_active ? "bg-emerald-200 text-emerald-900" : "bg-slate-300 text-slate-800"}`} data-testid={`risk-table-status-badge-${item.id}`}>
                    {item.is_active ? "ACTIVE" : item.lifecycle_state?.toUpperCase() || 'INACTIVE'}
                  </span>
                </TableCell>
                <TableCell data-testid={`risk-table-version-${item.id}`}>v{item.version_num}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-2">
                    <Button size="sm" variant="outline" className="border-slate-600 bg-transparent" onClick={() => editPolicy(item)} data-testid={`risk-table-edit-${item.id}`}>Düzenle</Button>
                    <Button size="sm" variant="outline" className="border-cyan-600 bg-transparent" onClick={() => previewPolicyImpact(item)} data-testid={`risk-table-preview-${item.id}`}>Preview</Button>
                    <Button size="sm" variant="outline" className="border-emerald-600 bg-transparent" onClick={() => activatePolicy(item)} data-testid={`risk-table-activate-${item.id}`}>Set Active</Button>
                    <Button size="sm" variant="outline" className="border-amber-600 bg-transparent" onClick={() => loadHistory(item)} data-testid={`risk-table-history-${item.id}`}>History</Button>
                    <Button size="sm" variant="outline" className="border-rose-600 bg-transparent" onClick={() => rollbackPolicy(item)} data-testid={`risk-table-rollback-${item.id}`}>Rollback</Button>
                    <Button size="sm" variant="outline" className="border-red-600 bg-transparent text-red-300 hover:text-red-200" onClick={() => deletePolicy(item)} data-testid={`risk-table-delete-${item.id}`}>Sil</Button>
                  </div>
                  <Input className="mt-2" value={reasonById[item.id] || ''} onChange={(event) => setReasonById((prev) => ({ ...prev, [item.id]: event.target.value }))} placeholder="reason" data-testid={`risk-table-reason-${item.id}`} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <div className="grid gap-4 lg:grid-cols-2" data-testid="risk-policy-observability-grid">
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="risk-policy-impact-preview-panel">
          <h3 className="text-base font-semibold" data-testid="risk-policy-impact-preview-title">Real-time Risk Impact Preview</h3>
          <pre className="mt-3 overflow-x-auto bg-slate-950 p-3 text-xs text-slate-200" data-testid="risk-policy-impact-preview-json">{JSON.stringify(previewImpact || {}, null, 2)}</pre>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="risk-policy-history-panel">
          <h3 className="text-base font-semibold" data-testid="risk-policy-history-title">Version / History / Rollback</h3>
          <pre className="mt-3 overflow-x-auto bg-slate-950 p-3 text-xs text-slate-200" data-testid="risk-policy-history-json">{JSON.stringify(history || [], null, 2)}</pre>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="risk-policy-enforce-panel">
          <h3 className="text-base font-semibold" data-testid="risk-policy-enforce-title">Execution Enforce Kanıtı</h3>
          <pre className="mt-3 overflow-x-auto bg-slate-950 p-3 text-xs text-slate-200" data-testid="risk-policy-enforce-json">{JSON.stringify(activePolicy?.metadata_json?.enforce || {}, null, 2)}</pre>
        </article>
        <article className="border border-slate-800 bg-slate-900 p-4" data-testid="risk-policy-kill-switch-panel">
          <h3 className="text-base font-semibold" data-testid="risk-policy-kill-switch-title">Kill Switch Görünürlüğü</h3>
          <pre className="mt-3 overflow-x-auto bg-slate-950 p-3 text-xs text-slate-200" data-testid="risk-policy-kill-switch-json">{JSON.stringify({ state: activePolicy?.metadata_json?.kill_switch || 'unknown', source: 'global', reason: activePolicy?.status_reason || null }, null, 2)}</pre>
        </article>
      </div>
    </section>
  );
};
